import React, { useEffect, useState } from 'react';
import {
  Package, Search, ShoppingCart, Percent, X,
  Grid3x3, List, Table as TableIcon, Tag, Layers
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import api from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import Carousel from '../../components/Carousel';
import { getMyProfile, type BuyerProfile } from '../../services/clientService';
import { ShieldAlert } from 'lucide-react';
import CategoryProductCatalog from '../../components/catalog/CategoryProductCatalog';

interface Consumable {
  id: number;
  title: string;
  description: string | null;
  marque: string | null;
  gamme: string | null;
  forme_galenique: string | null;
  conditionnement: string | null;
  mini_code: number | null;
  id_compl: number | null;
  code_barre: string | null;
  qr_code?: string | null;
  batch_size: number | null;
  min_order_quantity?: number | null;
  unit: string;
  volume_ml: number | null;
  base_price: number;
  prix_vente_conseille_ttc?: number | null;
  currency: string;
  is_active: boolean;
  stock_quantity: number | null;
  images?: string[] | null;
  created_at: string;
  updated_at: string;
  tiers?: Array<{
    tier_number: number;
    min_quantity: number;
    max_quantity?: number | null;
    discount_percentage?: number | null;
    free_units?: number | null;
  }>;
}

const BuyerCatalogTailwind: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdmin: boolean = user?.role === 'admin' || user?.role === 'super_admin' || user?.role === 'apporteur';
  const [consumables, setConsumables] = useState<any[]>([]);
  const [busy, setBusy] = useState<boolean>(false);
  const [search, setSearch] = useState<string>('');
  const [selectedProduct, setSelectedProduct] = useState<Consumable | null>(null);
  const [showDetailsModal, setShowDetailsModal] = useState<boolean>(false);
  const [detailsLoading, setDetailsLoading] = useState<boolean>(false);
  const [viewMode, setViewMode] = useState<'grid' | 'list' | 'table' | 'category'>(() =>
    typeof window !== 'undefined' && window.innerWidth < 640 ? 'list' : 'table'
  );
  const [profile, setProfile] = useState<BuyerProfile | null>(null);
  const [showBlockedModal, setShowBlockedModal] = useState<boolean>(false);

  useEffect(() => {
    void load();
  }, []);

  useEffect(() => {
    const t = setTimeout(() => void load(), 300);
    return () => clearTimeout(t);
  }, [search]);

  const load = async (): Promise<void> => {
    setBusy(true);
    try {
      // Charger le profil pour vérifier le statut
      const profileData = await getMyProfile();
      setProfile(profileData);

      // Charger uniquement les produits de la tarification active du Client
      const resPrices = await api.get('/clients/me/pricing');
      const products = Array.isArray(resPrices.data) ? resPrices.data : [];

      // Normaliser les images (parser si c'est une chaîne JSON)
      const normalizedProducts = products.map((product: any) => {
        let images = product.images;

        // Si images est une chaîne, essayer de la parser
        if (typeof images === 'string') {
          try {
            images = JSON.parse(images);
          } catch (e) {
            console.warn('Failed to parse images for product', product.id, e);
            images = [];
          }
        }

        // S'assurer que images est un tableau
        if (!Array.isArray(images)) {
          images = [];
        }

        return {
          ...product,
          images
        };
      });

      // Filtrer par recherche si nécessaire
      let filteredProducts = normalizedProducts;
      if (search) {
        const searchLower = search.toLowerCase();
        filteredProducts = normalizedProducts.filter((product: any) =>
          product.title?.toLowerCase().includes(searchLower) ||
          product.description?.toLowerCase().includes(searchLower) ||
          product.marque?.toLowerCase().includes(searchLower) ||
          product.gamme?.toLowerCase().includes(searchLower)
        );
      }

      setConsumables(filteredProducts);
    } catch (e) {
      console.error('Load catalog error:', e);
    } finally {
      setBusy(false);
    }
  };

  const getPrice = (product: any): { price: number; isCustom: boolean; discount: number | null } => {
    // Products from /clients/me/pricing already include custom pricing
    if (product.custom_price !== undefined) {
      return {
        price: product.custom_price,
        isCustom: true,
        discount: product.discount_percentage,
      };
    }
    return {
      price: product.base_price,
      isCustom: false,
      discount: null,
    };
  };

  const handleOrderProduct = (product: any): void => {
    // Vérifier si le compte est bloqué
    if (profile?.status === 'bloque') {
      setShowBlockedModal(true);
      return;
    }

    // Navigate to new order page with the selected product in URL params
    const priceInfo = getPrice(product);
    const productData = {
      id: product.consumable_id,
      title: product.title,
      price: priceInfo.price,
      unit: product.unit,
      currency: product.currency,
      batch_size: product.batch_size
    };

    // Pass product data via URL params
    const params = new URLSearchParams({
      product: JSON.stringify(productData)
    });

    navigate(`/client/orders/new?${params.toString()}`);
  };

  const handleViewDetails = (product: any): void => {
    console.log('[Catalog] Opening product details:', product);
    console.log('[Catalog] Product tiers:', product?.tiers);
    setSelectedProduct(product);
    setShowDetailsModal(true);
    void (async (): Promise<void> => {
      try {
        setDetailsLoading(true);
        const consumableId: number | undefined = product?.consumable_id ?? product?.id;
        if (!consumableId) return;

        const res = await api.get(`/consumables/${consumableId}`);
        const full = res.data as any;

        let images: unknown = full.images;
        if (typeof images === 'string') {
          try {
            images = JSON.parse(images);
          } catch {
            images = [];
          }
        }
        if (!Array.isArray(images)) images = [];

        setSelectedProduct((prev) => {
          const pricing = prev ?? product;
          const finalProduct = {
            ...full,
            images,
            consumable_id: consumableId,
            custom_price: pricing?.custom_price,
            discount_percentage: pricing?.discount_percentage,
            savings: pricing?.savings,
            pricing_reference: pricing?.pricing_reference,
            pricing_name: pricing?.pricing_name,
            pricing_id: pricing?.pricing_id,
            min_order_quantity: pricing?.min_order_quantity || full?.min_order_quantity,
            batch_size: pricing?.batch_size || full?.batch_size,
            qr_code: full?.qr_code, // Preserve QR code from consumable endpoint
            tiers: pricing?.tiers || product?.tiers || [], // Preserve tier data
          } as any;
          console.log('[Catalog] Final product with tiers:', finalProduct);
          console.log('[Catalog] Final tiers:', finalProduct.tiers);
          return finalProduct;
        });
      } catch (e) {
        console.error('Load product details error:', e);
      } finally {
        setDetailsLoading(false);
      }
    })();
  };

  const closeDetailsModal = (): void => {
    setShowDetailsModal(false);
    setSelectedProduct(null);
  };

  const BlockedAccountModal = () => (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-[60] p-4 animate-in fade-in duration-200">
      <div className="max-w-md w-full bg-white dark:bg-slate-900 rounded-2xl shadow-2xl border-2 border-red-100 dark:border-red-900/30 p-8 text-center animate-in zoom-in duration-300">
        <div className="w-20 h-20 bg-red-100 dark:bg-red-900/20 rounded-full flex items-center justify-center mx-auto mb-6">
          <ShieldAlert className="h-10 w-10 text-red-600 dark:text-red-400" />
        </div>
        <h2 className="text-2xl font-bold text-slate-900 dark:text-slate-100 mb-4">Compte Bloqué</h2>
        <p className="text-slate-600 dark:text-slate-400 mb-8">
          Votre compte est temporairement bloqué. Merci de contacter l’administrateur pour passer commande.
        </p>
        <button
          onClick={() => setShowBlockedModal(false)}
          className="w-full py-3 bg-slate-900 dark:bg-slate-800 text-white rounded-xl font-bold hover:bg-slate-800 dark:hover:bg-slate-700 transition-colors"
        >
          Compris
        </button>
      </div>
    </div>
  );

  const selectedProductPrice = selectedProduct ? getPrice(selectedProduct) : null;

  return (
    <div className="h-screen flex flex-col bg-gradient-to-br from-slate-50 via-blue-50/20 to-slate-50 dark:from-slate-950 dark:via-blue-950/10 dark:to-slate-950">
      {/* Header */}
      <div className="flex items-center justify-between px-3 py-3 sm:px-6 sm:py-4 bg-white/80 dark:bg-slate-900/80 backdrop-blur-xl border-b border-slate-200/50 dark:border-slate-700/50 shadow-lg">
        <div className="flex items-center gap-3 sm:gap-4">
          <div className="w-10 h-10 sm:w-12 sm:h-12 bg-gradient-to-br from-cyan-500 via-blue-500 to-cyan-600 rounded-xl flex items-center justify-center shadow-lg">
            <Package className="h-5 w-5 sm:h-6 sm:w-6 text-white" />
          </div>
          <div>
            <h1 className="text-xl sm:text-2xl font-bold text-slate-900 dark:text-slate-100">Produits</h1>
            <p className="text-xs sm:text-sm text-slate-500 dark:text-slate-400">
              {consumables.length} produit{consumables.length > 1 ? 's' : ''} disponible{consumables.length > 1 ? 's' : ''}
            </p>
          </div>
        </div>
      </div>

      {/* Search and View Switcher */}
      <div className="px-3 py-3 sm:px-6 sm:py-4 flex flex-row gap-2 sm:gap-4 justify-between items-center">
        <div className="relative flex-1 w-full sm:max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Rechercher un produit..."
            className="w-full h-10 pl-10 pr-4 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-sm focus:outline-none focus:ring-2 focus:ring-cyan-500"
          />
        </div>

        <div className="flex items-center gap-1 border border-slate-300 dark:border-slate-600 rounded-lg p-1 bg-white dark:bg-slate-800">
          <button
            onClick={() => setViewMode('grid')}
            className={`p-2 rounded-md transition ${viewMode === 'grid'
              ? 'bg-cyan-100 dark:bg-cyan-900/30 text-cyan-700 dark:text-cyan-300'
              : 'text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-700'
              }`}
            title="Vue Grille"
          >
            <Grid3x3 className="h-4 w-4" />
          </button>
          <button
            onClick={() => setViewMode('list')}
            className={`p-2 rounded-md transition ${viewMode === 'list'
              ? 'bg-cyan-100 dark:bg-cyan-900/30 text-cyan-700 dark:text-cyan-300'
              : 'text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-700'
              }`}
            title="Vue Liste"
          >
            <List className="h-4 w-4" />
          </button>
          <button
            onClick={() => setViewMode('table')}
            className={`hidden md:block p-2 rounded-md transition ${viewMode === 'table'
              ? 'bg-cyan-100 dark:bg-cyan-900/30 text-cyan-700 dark:text-cyan-300'
              : 'text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-700'
              }`}
            title="Vue Tableau"
          >
            <TableIcon className="h-4 w-4" />
          </button>
          <button
            onClick={() => setViewMode('category')}
            className={`p-2 rounded-md transition ${viewMode === 'category'
              ? 'bg-cyan-100 dark:bg-cyan-900/30 text-cyan-700 dark:text-cyan-300'
              : 'text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-700'
              }`}
            title="Vue par Catégorie"
          >
            <Layers className="h-4 w-4" />
          </button>
        </div>
      </div>

      {/* Content */}
      <div className={`flex-1 px-3 pb-3 sm:px-6 sm:pb-6 min-h-0 ${viewMode === 'table' ? 'overflow-hidden' : 'overflow-auto'}`}>
        {busy ? (
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-cyan-600"></div>
          </div>
        ) : consumables.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-64 text-slate-500 dark:text-slate-400">
            <Package className="h-16 w-16 mb-4 opacity-50" />
            <p className="text-lg font-medium">Aucun produit trouvé</p>
          </div>
        ) : viewMode === 'grid' ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {consumables.map((item: any) => {
              const priceInfo = getPrice(item);
              return (
                <div
                  key={item.id}
                  onClick={() => handleViewDetails(item)}
                  className="group relative p-5 rounded-xl border-2 border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 hover:shadow-xl hover:border-cyan-300 dark:hover:border-cyan-700 cursor-pointer transition-[border-color,box-shadow] duration-300"
                >
                  {/* Image Carousel */}
                  <div className="w-full aspect-square bg-slate-100 dark:bg-slate-800 rounded-lg mb-3 overflow-hidden">
                    <Carousel
                      images={item.images || []}
                      alt={item.title}
                      className="w-full h-full object-cover"
                      showDots={true}
                      showArrows={true}
                      autoPlay={false}
                    />
                  </div>

                  {/* Info */}
                  <h3 className="font-bold text-slate-900 dark:text-slate-100 mb-1 truncate">{item.title}</h3>
                  <p className="text-sm text-slate-600 dark:text-slate-400 mb-3 line-clamp-2 min-h-[40px]">{item.description || 'Aucune description'}</p>

                  <div className="space-y-1.5 mb-3">
                    {/* Prix de vente */}
                    <p className="text-xs text-slate-600 dark:text-slate-400">
                      Prix Catalogue : <span className="font-bold text-cyan-600 text-lg">{priceInfo.price.toFixed(2)} € HT</span> <span className="text-slate-500">/ {item.unit}</span>
                    </p>
                    {item.prix_vente_conseille_ttc != null && (
                      <p className="text-xs text-slate-600 dark:text-slate-400">
                        Prix de vente conseillé : <span className="font-bold text-cyan-600">{Number(item.prix_vente_conseille_ttc).toFixed(2)} € TTC</span> <span className="text-slate-500">/ {item.unit}</span>
                      </p>
                    )}
                    {/* Minimum de commande */}
                    <p className="text-xs text-slate-600 dark:text-slate-400">
                      Minimum de commande : <span className="font-medium">Lot de {item.batch_size || 1} {item.unit}</span>
                    </p>
                    {/* Remise à partir de n lots */}
                    {item.tiers && item.tiers.length > 0 && (() => {
                      const firstTier = [...item.tiers].sort((a: any, b: any) => a.min_quantity - b.min_quantity)[0];
                      return (
                        <p className="text-xs text-emerald-600 dark:text-emerald-400 font-medium">
                          Remise à partir de {firstTier.min_quantity} lot{firstTier.min_quantity > 1 ? 's' : ''}
                        </p>
                      );
                    })()}

                    {isAdmin && priceInfo.isCustom && priceInfo.discount !== null && (
                      <div className="flex items-center gap-1">
                        <div className="px-2 py-0.5 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center gap-1">
                          <Percent className="h-3 w-3 text-green-600 dark:text-green-400" />
                          <span className="text-xs text-green-600 dark:text-green-400 font-bold">
                            -{priceInfo.discount.toFixed(1)}%
                          </span>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Order Button */}
                  <button
                    onClick={(e) => { e.stopPropagation(); handleOrderProduct(item); }}
                    className="w-full flex items-center justify-center gap-2 px-3 py-2 bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-600 hover:to-blue-700 text-white rounded-lg shadow-md hover:shadow-lg hover:scale-[1.02] active:scale-95 transition-[background-image,transform,box-shadow] duration-200 text-sm font-medium"
                  >
                    <ShoppingCart className="h-4 w-4" />
                    Commander
                  </button>
                </div>
              );
            })}
          </div>
        ) : viewMode === 'list' ? (
          <>
            {/* Mobile list cards (< sm) */}
            <div className="sm:hidden space-y-2">
              {consumables.map((item: any, idx: number) => {
                const priceInfo = getPrice(item);
                return (
                  <div
                    key={item.id}
                    onClick={() => handleViewDetails(item)}
                    className="p-3 rounded-xl border-2 border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 hover:border-cyan-300 dark:hover:border-cyan-700 transition-all duration-300 cursor-pointer animate-in fade-in slide-in-from-left-4"
                    style={{ animationDelay: `${idx * 30}ms`, animationFillMode: 'backwards' }}
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-14 h-14 bg-slate-100 dark:bg-slate-800 rounded-lg overflow-hidden flex-shrink-0">
                        <Carousel
                          images={item.images || []}
                          alt={item.title}
                          className="w-full h-full"
                          showDots={false}
                          showArrows={false}
                          autoPlay={false}
                          compact={true}
                        />
                      </div>
                      <div className="flex-1 min-w-0">
                        <h3 className="font-bold text-sm text-slate-900 dark:text-slate-100 truncate">{item.title}</h3>
                        <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[10px] font-medium bg-slate-100 dark:bg-slate-800 text-slate-500 border border-slate-200 dark:border-slate-700">
                          <Tag className="h-2.5 w-2.5" />
                          {item.marque || 'Sans marque'}
                        </span>
                      </div>
                      <button
                        onClick={(e) => { e.stopPropagation(); handleOrderProduct(item); }}
                        className="p-2 bg-gradient-to-r from-cyan-500 to-blue-600 text-white rounded-lg shadow-md flex-shrink-0"
                      >
                        <ShoppingCart className="h-4 w-4" />
                      </button>
                    </div>
                    <div className="mt-2 pt-2 border-t border-slate-100 dark:border-slate-800 flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-slate-500">
                      <span>
                        <span className="font-bold text-cyan-600">{priceInfo.price.toFixed(2)} € HT</span> / {item.unit}
                      </span>
                      {item.prix_vente_conseille_ttc != null && (
                        <span>
                          PVC: <span className="font-bold text-cyan-600">{Number(item.prix_vente_conseille_ttc).toFixed(2)} € TTC</span>
                        </span>
                      )}
                      <span>Min: {item.batch_size || 1} {item.unit}</span>
                      {item.tiers && item.tiers.length > 0 && (() => {
                        const firstTier = [...item.tiers].sort((a: any, b: any) => a.min_quantity - b.min_quantity)[0];
                        return (
                          <span className="text-emerald-600 dark:text-emerald-400 font-medium">
                            Remise dès {firstTier.min_quantity} lot{firstTier.min_quantity > 1 ? 's' : ''}
                          </span>
                        );
                      })()}
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Desktop list (sm+) — original layout */}
            <div className="hidden sm:block space-y-3">
              {consumables.map((item: any, idx: number) => {
                const priceInfo = getPrice(item);
                return (
                  <div
                    key={item.id}
                    onClick={() => handleViewDetails(item)}
                    className="flex items-center gap-4 p-4 rounded-xl border-2 border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 hover:shadow-lg hover:border-cyan-300 dark:hover:border-cyan-700 hover:translate-x-1 transition-[border-color,box-shadow,transform] duration-300 ease-out animate-in fade-in slide-in-from-left-4 cursor-pointer"
                    style={{ animationDelay: `${idx * 30}ms`, animationFillMode: 'backwards' }}
                  >
                    <div className="w-20 h-20 bg-slate-100 dark:bg-slate-800 rounded-lg overflow-hidden flex-shrink-0">
                      <Carousel
                        images={item.images || []}
                        alt={item.title}
                        className="w-full h-full"
                        showDots={false}
                        showArrows={false}
                        autoPlay={false}
                        compact={true}
                      />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <h3 className="font-bold text-slate-900 dark:text-slate-100 truncate">{item.title}</h3>
                        {isAdmin && priceInfo.isCustom && priceInfo.discount !== null && (
                          <div className="px-2 py-0.5 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center gap-1">
                            <Percent className="h-3 w-3 text-green-600 dark:text-green-400" />
                            <span className="text-xs text-green-600 dark:text-green-400 font-bold">
                              -{priceInfo.discount.toFixed(1)}%
                            </span>
                          </div>
                        )}
                      </div>
                      <p className="text-sm text-slate-600 dark:text-slate-400 line-clamp-1">{item.description || 'Aucune description'}</p>
                      <div className="flex items-center gap-2 mt-2">
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-400 border border-slate-200 dark:border-slate-700">
                          <Tag className="h-3 w-3" />
                          {item.marque || 'Sans marque'}
                        </span>
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="flex flex-col items-end gap-1">
                        <p className="text-xs text-slate-500">
                          Prix Catalogue : <span className="font-bold text-cyan-600">{priceInfo.price.toFixed(2)} € HT</span> / {item.unit}
                        </p>
                        {item.prix_vente_conseille_ttc != null && (
                          <p className="text-xs text-slate-500">
                            Prix de vente conseillé : <span className="font-bold text-cyan-600">{Number(item.prix_vente_conseille_ttc).toFixed(2)} € TTC</span> / {item.unit}
                          </p>
                        )}
                        <p className="text-xs text-slate-500">
                          Min: Lot de {item.batch_size || 1} {item.unit}
                        </p>
                        {item.tiers && item.tiers.length > 0 && (() => {
                          const firstTier = [...item.tiers].sort((a: any, b: any) => a.min_quantity - b.min_quantity)[0];
                          return (
                            <p className="text-xs text-emerald-600 dark:text-emerald-400 font-medium">
                              Remise dès {firstTier.min_quantity} lot{firstTier.min_quantity > 1 ? 's' : ''}
                            </p>
                          );
                        })()}
                      </div>
                    </div>
                    {item.stock_quantity !== null && (
                      <div className="text-center px-4">
                        <div className="text-xs text-slate-500 mb-1">Stock</div>
                        <div className="font-bold text-slate-700 dark:text-slate-300">{item.stock_quantity}</div>
                      </div>
                    )}
                    <div className="flex gap-2 pl-4 border-l border-slate-200 dark:border-slate-700">
                      <button
                        onClick={(e) => { e.stopPropagation(); handleOrderProduct(item); }}
                        className="flex items-center justify-center gap-2 px-3 py-2 bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-600 hover:to-blue-700 text-white rounded-lg shadow-md hover:shadow-lg transition-[background-image,box-shadow] duration-200 text-sm font-medium"
                      >
                        <ShoppingCart className="h-4 w-4" />
                        <span>Commander</span>
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          </>

        ) : viewMode === 'table' ? (
          <div className="bg-white dark:bg-slate-900 rounded-xl border-2 border-slate-200 dark:border-slate-700 shadow-lg h-full flex flex-col min-h-0">
            <div className="flex-1 overflow-auto min-h-0">
              <table className="w-full">
                <thead className="bg-gradient-to-r from-slate-50 to-blue-50 dark:from-slate-900 dark:to-blue-950/30 border-b-2 border-slate-200 dark:border-slate-700 sticky top-0 z-10">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-bold text-slate-700 dark:text-slate-300 uppercase tracking-wider bg-slate-50/95 dark:bg-slate-900/95 backdrop-blur">Produit</th>
                    <th className="px-4 py-3 text-left text-xs font-bold text-slate-700 dark:text-slate-300 uppercase tracking-wider bg-slate-50/95 dark:bg-slate-900/95 backdrop-blur hidden md:table-cell">Description</th>
                    <th className="px-4 py-3 text-left text-xs font-bold text-slate-700 dark:text-slate-300 uppercase tracking-wider bg-slate-50/95 dark:bg-slate-900/95 backdrop-blur hidden sm:table-cell">Marque</th>
                    <th className="px-4 py-3 text-left text-xs font-bold text-slate-700 dark:text-slate-300 uppercase tracking-wider bg-slate-50/95 dark:bg-slate-900/95 backdrop-blur">Prix</th>
                    <th className="px-4 py-3 text-center text-xs font-bold text-slate-700 dark:text-slate-300 uppercase tracking-wider bg-slate-50/95 dark:bg-slate-900/95 backdrop-blur">Stock</th>
                    <th className="px-4 py-3 text-right text-xs font-bold text-slate-700 dark:text-slate-300 uppercase tracking-wider bg-slate-50/95 dark:bg-slate-900/95 backdrop-blur">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-200 dark:divide-slate-700">
                  {consumables.map((item: any, idx: number) => {
                    const priceInfo = getPrice(item);
                    return (
                      <tr
                        key={item.id}
                        onClick={() => handleViewDetails(item)}
                        className="hover:bg-blue-50/50 dark:hover:bg-blue-950/20 transition duration-200 animate-in fade-in slide-in-from-bottom-2 cursor-pointer"
                        style={{ animationDelay: `${idx * 20}ms`, animationFillMode: 'backwards' }}
                      >
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 bg-slate-100 dark:bg-slate-800 rounded-lg overflow-hidden flex-shrink-0">
                              <Carousel
                                images={item.images || []}
                                alt={item.title}
                                className="w-full h-full"
                                showDots={false}
                                showArrows={false}
                                autoPlay={false}
                                compact={true}
                              />
                            </div>
                            <div>
                              <div className="font-semibold text-slate-900 dark:text-slate-100">{item.title}</div>
                              <div className="text-xs text-slate-500 sm:hidden">{item.marque}</div>
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-3 text-sm text-slate-600 dark:text-slate-400 max-w-xs truncate hidden md:table-cell">
                          {item.description || '—'}
                        </td>
                        <td className="px-4 py-3 text-sm text-slate-600 dark:text-slate-400 hidden sm:table-cell">
                          {item.marque || '—'}
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex flex-col gap-0.5">
                            {/* Prix de vente */}
                            <span className="text-xs text-slate-500">
                              Prix Catalogue : <span className="font-bold text-cyan-600">{priceInfo.price.toFixed(2)} € HT</span> / {item.unit}
                            </span>
                            {item.prix_vente_conseille_ttc != null && (
                              <span className="text-xs text-slate-500">
                                Prix de vente conseillé : <span className="font-bold text-cyan-600">{Number(item.prix_vente_conseille_ttc).toFixed(2)} € TTC</span> / {item.unit}
                              </span>
                            )}
                            {/* Minimum de commande */}
                            <span className="text-xs text-slate-500">
                              Min: Lot de {item.batch_size || 1} {item.unit}
                              {/* {(item.batch_size || 1) > 1 ? 's' : ''} */}
                            </span>
                            {/* Remise à partir de n lots */}
                            {item.tiers && item.tiers.length > 0 && (() => {
                              const firstTier = [...item.tiers].sort((a: any, b: any) => a.min_quantity - b.min_quantity)[0];
                              return (
                                <span className="text-xs text-emerald-600 dark:text-emerald-400 font-medium">
                                  Remise dès {firstTier.min_quantity} lot{firstTier.min_quantity > 1 ? 's' : ''}
                                </span>
                              );
                            })()}
                          </div>
                        </td>
                        <td className="px-4 py-3 text-center">
                          {item.stock_quantity !== null ? (
                            <span className="font-medium text-slate-700 dark:text-slate-300">{item.stock_quantity}</span>
                          ) : (
                            <span className="text-sm text-slate-400">—</span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-right">
                          <button
                            onClick={(e) => { e.stopPropagation(); handleOrderProduct(item); }}
                            className="inline-flex items-center justify-center p-2 bg-blue-50 text-blue-600 hover:bg-blue-100 dark:bg-blue-900/30 dark:text-blue-400 dark:hover:bg-blue-900/50 rounded-lg transition"
                            title="Commander"
                          >
                            <ShoppingCart className="h-4 w-4" />
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        ) : viewMode === 'category' ? (
          <CategoryProductCatalog
            products={consumables}
            onProductClick={handleViewDetails}
            onOrderProduct={handleOrderProduct}
            getPrice={getPrice}
            isAdmin={isAdmin}
          />
        ) : null}
      </div>

      {/* Product Details Modal */}
      {showDetailsModal && selectedProduct && selectedProductPrice && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4 animate-in fade-in duration-200">
          <div className="bg-white dark:bg-slate-900 rounded-2xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto animate-in zoom-in-95 slide-in-from-bottom-4 duration-300">
            <div className="flex items-center justify-between p-6 border-b border-slate-200 dark:border-slate-700 sticky top-0 bg-white dark:bg-slate-900 z-10">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-gradient-to-br from-cyan-500 to-blue-600 rounded-lg flex items-center justify-center">
                  <Package className="h-5 w-5 text-white" />
                </div>
                <div>
                  <h2 className="text-xl font-bold text-slate-900 dark:text-slate-100">Détails du produit</h2>
                  <p className="text-sm text-slate-500 dark:text-slate-400">{selectedProduct.title}</p>
                </div>
              </div>
              <button
                onClick={closeDetailsModal}
                className="p-2 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg transition"
              >
                <X className="h-5 w-5 text-slate-500" />
              </button>
            </div>

            <div className="p-6 space-y-6">
              {/* Product Images */}
              {selectedProduct.images && selectedProduct.images.length > 0 && (
                <div className="w-full aspect-video bg-slate-100 dark:bg-slate-800 rounded-xl overflow-hidden shadow-inner">
                  <Carousel
                    images={selectedProduct.images}
                    alt={selectedProduct.title}
                    className="w-full h-full"
                    showDots={true}
                    showArrows={true}
                    autoPlay={false}
                  />
                </div>
              )}

              {/* Description */}
              {selectedProduct.description && (
                <div className="bg-gradient-to-br from-slate-50 to-slate-100 dark:from-slate-800/50 dark:to-slate-800/30 rounded-xl p-5 border border-slate-200 dark:border-slate-700">
                  <h3 className="text-sm font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wide mb-2">Description</h3>
                  <p className="text-slate-700 dark:text-slate-300 leading-relaxed">{selectedProduct.description}</p>
                </div>
              )}

              {/* Product Information Cards */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Basic Information Card */}
                <div className="bg-white dark:bg-slate-800 rounded-xl p-5 border border-slate-200 dark:border-slate-700 shadow-sm">
                  <h3 className="text-sm font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wide mb-4 flex items-center gap-2">
                    <Package className="h-4 w-4" />
                    Informations générales
                  </h3>
                  <div className="space-y-3">
                    <div className="flex justify-between items-start py-2 border-b border-slate-100 dark:border-slate-700/50">
                      <span className="text-sm text-slate-500 dark:text-slate-400">Marque</span>
                      <span className="text-sm font-medium text-slate-900 dark:text-slate-100 text-right">{selectedProduct.marque || '—'}</span>
                    </div>
                    <div className="flex justify-between items-start py-2 border-b border-slate-100 dark:border-slate-700/50">
                      <span className="text-sm text-slate-500 dark:text-slate-400">Gamme</span>
                      <span className="text-sm font-medium text-slate-900 dark:text-slate-100 text-right max-w-[60%]">{selectedProduct.gamme || '—'}</span>
                    </div>
                    <div className="flex justify-between items-start py-2 border-b border-slate-100 dark:border-slate-700/50">
                      <span className="text-sm text-slate-500 dark:text-slate-400">Forme galénique</span>
                      <span className="text-sm font-medium text-slate-900 dark:text-slate-100 text-right">{selectedProduct.forme_galenique || '—'}</span>
                    </div>
                    {/* <div className="flex justify-between items-start py-2">
                      <span className="text-sm text-slate-500 dark:text-slate-400">Conditionnement</span>
                      <span className="text-sm font-medium text-slate-900 dark:text-slate-100 text-right max-w-[60%]">{selectedProduct.conditionnement || '—'}</span>
                    </div> */}
                  </div>
                </div>

                {/* Technical Information Card */}
                <div className="bg-white dark:bg-slate-800 rounded-xl p-5 border border-slate-200 dark:border-slate-700 shadow-sm">
                  <h3 className="text-sm font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wide mb-4 flex items-center gap-2">
                    <Tag className="h-4 w-4" />
                    Informations techniques
                  </h3>
                  <div className="space-y-3">
                    <div className="flex justify-between items-start py-2 border-b border-slate-100 dark:border-slate-700/50">
                      <span className="text-sm text-slate-500 dark:text-slate-400">Unité</span>
                      <span className="text-sm font-medium text-slate-900 dark:text-slate-100 text-right">
                        {selectedProduct.unit || '—'}
                      </span>
                    </div>
                    <div className="flex justify-between items-start py-2 border-b border-slate-100 dark:border-slate-700/50">
                      <span className="text-sm text-slate-500 dark:text-slate-400">Qté par Lot ({selectedProduct.unit})</span>
                      <span className="text-sm font-medium text-slate-900 dark:text-slate-100 text-right">
                        {selectedProduct.batch_size ? (selectedProduct.batch_size === 1 ? 'Vendu à l\'unité' : `Lot de ${selectedProduct.batch_size} unité`) : '—'}
                      </span>
                    </div>
                    {selectedProduct.min_order_quantity && selectedProduct.min_order_quantity > 1 && (
                      <div className="flex justify-between items-start py-2 border-b border-slate-100 dark:border-slate-700/50">
                        <span className="text-sm text-slate-500 dark:text-slate-400">Minimum de commande (Lot)</span>
                        <span className="text-sm font-semibold text-orange-600 dark:text-orange-400 text-right">
                          {selectedProduct.min_order_quantity} {selectedProduct.min_order_quantity === 1 ? 'lot' : 'lots'}
                        </span>
                      </div>
                    )}
                    <div className="flex justify-between items-start py-2 border-b border-slate-100 dark:border-slate-700/50">
                      <span className="text-sm text-slate-500 dark:text-slate-400">Référence</span>
                      <span className="text-xs font-mono text-slate-900 dark:text-slate-100 text-right">
                        {(selectedProduct.mini_code ?? selectedProduct.id_compl) ? String(selectedProduct.mini_code ?? selectedProduct.id_compl) : '—'}
                      </span>
                    </div>
                    <div className="flex justify-between items-start py-2 border-b border-slate-100 dark:border-slate-700/50">
                      <span className="text-sm text-slate-500 dark:text-slate-400">Code barre</span>
                      <span className="text-xs font-mono text-slate-900 dark:text-slate-100 text-right">{selectedProduct.code_barre || '—'}</span>
                    </div>
                    {selectedProduct.qr_code && (
                      <div className="flex justify-between items-start py-2 border-b border-slate-100 dark:border-slate-700/50">
                        <span className="text-sm text-slate-500 dark:text-slate-400">QR code</span>
                        <div className="flex items-center gap-2">
                          <img
                            src={selectedProduct.qr_code.startsWith('http') ? selectedProduct.qr_code : `${import.meta.env.VITE_API_URL || 'http://localhost:3000'}${selectedProduct.qr_code}`}
                            alt="QR Code"
                            className="w-16 h-16 border border-slate-300 dark:border-slate-600 rounded object-contain"
                            onError={(e) => {
                              e.currentTarget.style.display = 'none';
                              console.error('Failed to load QR code:', selectedProduct.qr_code);
                            }}
                          />
                        </div>
                      </div>
                    )}
                    {/* <div className="flex justify-between items-start py-2">
// ... (rest of the code remains the same)
                      <span className="text-sm font-semibold text-green-600 dark:text-green-400 text-right">
                        {selectedProduct.stock_quantity !== null ? `${selectedProduct.stock_quantity} unités` : '—'}
                      </span>
                    </div> */}
                  </div>
                </div>
              </div>

              {/* Pricing Card - Highlighted */}
              <div className="bg-gradient-to-br from-cyan-50 to-blue-50 dark:from-cyan-950/30 dark:to-blue-950/30 rounded-xl p-6 border-2 border-cyan-200 dark:border-cyan-800 shadow-lg">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <h3 className="text-sm font-semibold text-cyan-700 dark:text-cyan-300 uppercase tracking-wide mb-3">Prix</h3>
                    <div className="flex items-baseline gap-2">
                      <p className="text-4xl font-bold text-cyan-600 dark:text-cyan-400">
                        {selectedProductPrice.price.toFixed(2)}
                      </p>
                      <span className="text-xl text-cyan-600 dark:text-cyan-400">{selectedProduct.currency} HT</span>
                    </div>
                    {selectedProduct.prix_vente_conseille_ttc != null && (
                      <p className="text-sm text-slate-600 dark:text-slate-400 mt-2">
                        Prix de vente conseillé : <span className="font-semibold text-cyan-700 dark:text-cyan-300">{Number(selectedProduct.prix_vente_conseille_ttc).toFixed(2)} {selectedProduct.currency} TTC</span>
                      </p>
                    )}
                    <p className="text-sm text-slate-600 dark:text-slate-400 mt-2">
                      / {selectedProduct.unit}
                    </p>
                    {detailsLoading && (
                      <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">Chargement des détails…</p>
                    )}
                    {selectedProduct.batch_size && selectedProduct.batch_size > 1 && (
                      <div className="mt-3 pt-3 border-t border-cyan-200 dark:border-cyan-800">
                        <p className="text-sm text-slate-600 dark:text-slate-400">
                          <span className="font-semibold text-cyan-700 dark:text-cyan-300">
                            {(selectedProductPrice.price * selectedProduct.batch_size).toFixed(2)} {selectedProduct.currency} HT
                          </span>
                          {' '}par lot de {selectedProduct.batch_size} unité
                        </p>
                      </div>
                    )}
                  </div>

                  {/* Show base discount if no tiers but has discount */}
                  {selectedProductPrice.isCustom && selectedProductPrice.discount != null && selectedProductPrice.discount > 0 && (!selectedProduct.tiers || selectedProduct.tiers.length === 0) && (
                    <div className="ml-4">
                      <div className="bg-green-100 dark:bg-green-900/30 px-3 py-2 rounded-lg">
                        <div className="flex items-center gap-1">
                          <Percent className="h-4 w-4 text-green-600 dark:text-green-400" />
                          <span className="text-lg font-bold text-green-600 dark:text-green-400">
                            -{selectedProductPrice.discount.toFixed(1)}%
                          </span>
                        </div>
                        <p className="text-xs text-green-700 dark:text-green-300 mt-1">Remise</p>
                      </div>
                    </div>
                  )}
                  {/* Show tier offers (both UG and percentage) */}
                  {selectedProduct.tiers && selectedProduct.tiers.length > 0 && (
                    <div className="ml-4">
                      <div className="bg-emerald-100 dark:bg-emerald-900/30 px-3 py-2 rounded-lg">
                        <p className="text-xs font-semibold text-emerald-700 dark:text-emerald-300 mb-1">🎁 Offres paliers:</p>
                        {selectedProduct.tiers.map((tier: any) => (
                          <p key={tier.tier_number} className="text-xs text-emerald-600 dark:text-emerald-400">
                            {tier.min_quantity}+ unités: {tier.free_units && tier.free_units > 0 ? `+${tier.free_units} gratuit` : tier.discount_percentage && tier.discount_percentage > 0 ? `-${tier.discount_percentage}% remise` : 'Prix spécial'}
                          </p>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex justify-end gap-3 pt-4 border-t border-slate-200 dark:border-slate-700">
                <button
                  onClick={closeDetailsModal}
                  className="px-4 py-2 rounded-lg border border-slate-300 dark:border-slate-600 hover:bg-slate-50 dark:hover:bg-slate-800 transition"
                >
                  Fermer
                </button>
                <button
                  onClick={() => {
                    handleOrderProduct(selectedProduct);
                    closeDetailsModal();
                  }}
                  className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-600 hover:to-blue-700 text-white rounded-lg shadow-md hover:shadow-lg transition-[background-image,box-shadow] duration-200"
                >
                  <ShoppingCart className="h-4 w-4" />
                  Commander ce produit
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {showBlockedModal && <BlockedAccountModal />}
    </div>
  );
};

export default BuyerCatalogTailwind;
